/**
 * ADR-0206 §4.2: pure helpers for the MCP usage tab on the tenant admin AI
 * usage screen — the group-by options, the columns each breakdown shows, the
 * `—` for a missing name, and the absolute-time captions. Kept in lib/ so they
 * are unit-testable without rendering (erratum E2; console convention).
 *
 * Everything the tab shows is SERVED: names are resolved by the hub, rows
 * carry no ids (the console keys them by index), and times render absolute in
 * the tenant's zone — never relative.
 */

export type McpUsageGroupBy =
  | "person"
  | "tool"
  | "person_tool"
  | "app"
  | "day"
  // The Subjects view: what people bring to Atlas, and how their ground moved under them.
  | "subjects"
  | "subjects_person"
  | "subjects_day";

/** The three groupings served from the subject log rather than the tool-call record. */
export function isSubjectGroup(groupBy: McpUsageGroupBy): boolean {
  return groupBy === "subjects" || groupBy === "subjects_person" || groupBy === "subjects_day";
}

/** The subject log's summary for the range — served beside the tool-call one on a subjects grouping. */
export type McpSubjectSummary = {
  opens: number;
  people: number;
  known: number;
  partly_known: number;
  broad: number;
  novel: number;
  refines: number;
  moves: number;
  holds: number;
  closes: number;
  closes_with_learning: number;
  restated_served: number;
  recorded_since: string | null;
};

/** The engine's own verdict, as the log keeps it; `broad` and `novel` on the row say what was served. */
export type SubjectVerdict = "known" | "partly_known" | "ambiguous" | "no_match" | "novel";

export type McpUsageSummary = {
  calls: number;
  error_calls: number;
  people: number;
  tools: number;
  /** The tenant's earliest recorded call — the measurement epoch; null before any. */
  recorded_since: string | null;
};

export type McpUsageRow = {
  person?: string | null;
  tool?: string;
  app?: string | null;
  day?: string;
  calls: number;
  error_calls?: number;
  tools?: number;
  people?: number;
  last_call_at?: string | null;
  // A subject row (grouping `subjects`).
  /** The per-tenant number a redaction names — a number, never an id. */
  subject_number?: number;
  subject?: string;
  goal?: string | null;
  verdict?: SubjectVerdict;
  broad?: boolean;
  novel?: boolean;
  refines?: number;
  moves?: number;
  holds?: number;
  closed?: boolean;
  learned?: number | null;
  restated_served?: number;
  redacted?: boolean;
  opened_at?: string;
  // Subjects by person / by day.
  opens?: number;
  closes?: number;
};

export type McpUsagePayload = {
  summary: McpUsageSummary;
  /** Present on a subjects grouping. */
  subject_summary?: McpSubjectSummary;
  breakdown: McpUsageRow[];
  /** The tenant's zone; Last call, Day and the caption render in it. */
  time_zone: string;
};

/** The Group by select, in the owner's order — By person first (the default). */
export const MCP_USAGE_GROUP_OPTIONS: ReadonlyArray<{ value: McpUsageGroupBy; label: string }> = [
  { value: "person", label: "By person" },
  { value: "tool", label: "By tool" },
  { value: "person_tool", label: "By person and tool" },
  { value: "app", label: "By app" },
  { value: "day", label: "By day" },
  { value: "subjects", label: "Subjects" },
  { value: "subjects_person", label: "Subjects by person" },
  { value: "subjects_day", label: "Subjects by day" },
];

export type McpUsageColumn = { key: keyof McpUsageRow; label: string };

const PERSON: McpUsageColumn = { key: "person", label: "Person" };
const TOOL: McpUsageColumn = { key: "tool", label: "Tool" };
const APP: McpUsageColumn = { key: "app", label: "App" };
const DAY: McpUsageColumn = { key: "day", label: "Day" };
const CALLS: McpUsageColumn = { key: "calls", label: "Calls" };
const ERRORS: McpUsageColumn = { key: "error_calls", label: "Errors" };
const PEOPLE: McpUsageColumn = { key: "people", label: "People" };
const TOOLS_USED: McpUsageColumn = { key: "tools", label: "Tools used" };
const LAST_CALL: McpUsageColumn = { key: "last_call_at", label: "Last call" };
// The Subjects view.
const OPENED: McpUsageColumn = { key: "opened_at", label: "Opened" };
const SUBJECT: McpUsageColumn = { key: "subject", label: "Subject" };
const VERDICT: McpUsageColumn = { key: "verdict", label: "Verdict" };
const REFINES: McpUsageColumn = { key: "refines", label: "Refines" };
const MOVES: McpUsageColumn = { key: "moves", label: "Moves" };
const HOLDS: McpUsageColumn = { key: "holds", label: "Holds" };
const LEARNED: McpUsageColumn = { key: "learned", label: "Learned" };
const OPENS: McpUsageColumn = { key: "opens", label: "Subjects" };
const NOVEL: McpUsageColumn = { key: "novel", label: "New" };
const CLOSES: McpUsageColumn = { key: "closes", label: "Closed" };

/** The breakdown table's columns per grouping — one datum each (spec §4.2). */
export function mcpUsageColumns(groupBy: McpUsageGroupBy): McpUsageColumn[] {
  switch (groupBy) {
    case "person":
      return [PERSON, CALLS, ERRORS, TOOLS_USED, LAST_CALL];
    case "tool":
      return [TOOL, CALLS, ERRORS, PEOPLE, LAST_CALL];
    case "person_tool":
      return [PERSON, TOOL, CALLS, ERRORS, LAST_CALL];
    case "app":
      return [APP, CALLS, PEOPLE, LAST_CALL];
    case "day":
      return [DAY, CALLS, ERRORS, PEOPLE];
    case "subjects":
      return [OPENED, PERSON, SUBJECT, VERDICT, REFINES, MOVES, HOLDS, LEARNED];
    case "subjects_person":
      return [PERSON, OPENS, NOVEL, REFINES, MOVES, HOLDS, CLOSES];
    case "subjects_day":
      return [DAY, OPENS, NOVEL, REFINES, MOVES, HOLDS, CLOSES];
  }
}

/** A subject's verdict as it was served: Broad or New when the row says so, else the engine's word. */
export function verdictLabel(verdict: SubjectVerdict | undefined, broad: boolean | undefined, novel: boolean | undefined): string {
  if (novel || verdict === "no_match" || verdict === "novel") return "New";
  if (broad || verdict === "ambiguous") return "Broad";
  switch (verdict) {
    case "known":
      return "Known";
    case "partly_known":
      return "Partly known";
    default:
      return "—";
  }
}

/** What closing the subject recorded: not closed (—), "Nothing", or how many proposals and findings. */
export function learnedLabel(closed: boolean | undefined, learned: number | null | undefined): string {
  if (!closed || learned == null) return "—";
  return learned === 0 ? "Nothing" : learned.toLocaleString();
}

/** The subjects caption: when the subject record began. Absolute, in the tenant's zone. */
export function subjectsRecordedSinceCaption(iso: string | null, timeZone: string, locale?: string): string | null {
  const calls = recordedSinceCaption(iso, timeZone, locale);
  return calls === null ? null : calls.replace(/^Calls that finished, recorded since /, "Subjects opened, recorded since ");
}

/** A person whose account is gone, or an app that reported nothing: `—`, never blank, never an id. */
export function dashIfNull(value: string | null | undefined): string {
  return value == null || value === "" ? "—" : value;
}

/** The zone to render in: the tenant's, or UTC when it is unset or unknown to this browser. */
function zoneOrUtc(timeZone: string): string {
  try {
    new Intl.DateTimeFormat(undefined, { timeZone });
    return timeZone;
  } catch {
    return "UTC";
  }
}

/**
 * The one-line caption under the table: when the record began. Absolute, in
 * the tenant's zone. Null when nothing has ever been recorded — the caption is
 * then omitted.
 */
export function recordedSinceCaption(iso: string | null, timeZone: string, locale?: string): string | null {
  if (!iso) return null;
  const date = new Date(iso).toLocaleDateString(locale, {
    timeZone: zoneOrUtc(timeZone || "UTC"),
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  return `Calls that finished, recorded since ${date}.`;
}

/** Last call: absolute `toLocaleString` in the tenant's zone — never relative. */
export function formatLastCall(iso: string | null | undefined, timeZone: string, locale?: string): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(locale, { timeZone: zoneOrUtc(timeZone || "UTC") });
}
